import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import type { EntityIcon, ItemRow, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { type FileType, fileType } from "../../lib/fileTypes.ts";
import { outsideOf, spaceUrl } from "../../lib/paths.ts";
import { peek, type Side, sideFor } from "../../lib/peek.ts";
import { usesPills } from "../../lib/state.ts";
import { fileNameOf, titleOf } from "../../lib/titles.ts";
import { Link } from "../../ui/Button.tsx";
import { EntityGlyph } from "../../ui/EntityGlyph.tsx";
import { FILE_ICONS, KIND_ICONS, NAV_ICONS, PaperclipIcon as Paperclip, PeekIcon } from "../../ui/icons.tsx";
import { FOCUS, LINK } from "../../ui/styles.ts";
import { EntityLabel } from "./entityLabel.tsx";

/**
 * Each sort of file's color, by convention: Office blue text, green spreadsheets, yellow presentations, red PDFs;
 * Apple's iWork the other way round (yellow-orange text, blue presentations), in tints apart from Office's.
 */
const FILE_COLORS: Record<FileType, string> = {
  "revisa-doc": "text-indigo-600",
  "revisa-sheet": "text-teal-600",
  pdf: "text-red-600",
  word: "text-blue-700",
  "text-document": "text-blue-600",
  pages: "text-orange-500",
  excel: "text-green-700",
  spreadsheet: "text-green-600",
  numbers: "text-emerald-500",
  presentation: "text-amber-500",
  keynote: "text-sky-500",
  image: "text-violet-600",
  video: "text-pink-600",
  audio: "text-fuchsia-600",
  archive: "text-stone-500",
  code: "text-slate-600",
  text: "text-zinc-500",
};

/** A file item's sort (PDF, spreadsheet…), from its type and name; null for anything else. */
const sortOf = (item: Pick<ItemRow, "kind" | "file" | "title">): FileType | null =>
  item.file && item.kind === "file" ? fileType(item.file.mime, fileNameOf(item)) : null;

/**
 * What an item is (email, message, web page, file), as an icon with its name for screen readers; a file
 * by its sort (PDF, spreadsheet…), in that sort's color.
 */
export function KindIcon({ item }: { item: Pick<ItemRow, "kind" | "file" | "title"> }) {
  const { kind } = item;
  const sort = sortOf(item);
  const label = sort ? t(`fileType.${sort}`) : t(`itemKind.${kind}`);
  const Glyph = sort ? FILE_ICONS[sort] : KIND_ICONS[kind];
  return (
    <span class={`flex h-5 items-center ${sort ? FILE_COLORS[sort] : "text-zinc-500"}`} title={label}>
      <Glyph />
      <span class="sr-only">{label}</span>
    </span>
  );
}

/**
 * `KindIcon` before an item's link in a list (a row, a card): a button that opens the item in the side panel, the
 * page staying as it is (its link still goes to its page). Pointed at, it turns into the panel's icon, and says so:
 * its arrow toward the side the panel opens on. Its kind not known yet (`kind` missing): an item's icon meanwhile.
 */
export function PeekKindIcon({
  space,
  item,
}: {
  space: SpaceOut;
  item: Pick<ItemRow, "id" | "title"> & Partial<Pick<ItemRow, "kind" | "file">>;
}) {
  const { kind } = item;
  const icon = kind ? (
    <KindIcon item={{ ...item, kind }} />
  ) : (
    <span class="flex h-5 items-center text-zinc-300">
      <NAV_ICONS.items />
    </span>
  );
  return <PeekButton space={space} id={item.id} name={titleOf(item)} icon={icon} />;
}

/**
 * `icon` (what something is) before its link in a list, as a button opening it in the side panel (an item, a folder),
 * pointed at turning into the panel's icon toward the side it opens on.
 */
export function PeekButton({
  space,
  id,
  name,
  icon,
}: {
  space: SpaceOut;
  id: string;
  name: string;
  icon: ComponentChildren;
}) {
  const [side, setSide] = useState<Side>("right");
  if (outsideOf(space)) {
    return <>{icon}</>;
  }
  const aim = (e: { currentTarget: HTMLElement }) => setSide(sideFor(e.currentTarget));
  return (
    <button
      type="button"
      title={t("peek.lookBeside")}
      aria-label={t("peek.lookAt", { title: name })}
      class={`group/peek -m-0.5 flex shrink-0 cursor-pointer rounded p-0.5 hover:bg-zinc-100 ${FOCUS}`}
      onPointerEnter={aim}
      onFocus={aim}
      onClick={(e) => peek(id, e.currentTarget)}
    >
      <span class="group-hover/peek:hidden group-focus-visible/peek:hidden">{icon}</span>
      <span
        class={`hidden h-5 items-center text-zinc-700 group-hover/peek:flex group-focus-visible/peek:flex ${side === "left" ? "-scale-x-100" : ""}`}
      >
        <PeekIcon />
      </span>
    </button>
  );
}

/**
 * An item in two lines, as a list's cell shows it: its kind (the side panel's button) and its title (to its page);
 * below, its entity's icon and name. Icons in one column, words in another. `entity` undefined: none shown (a list of
 * one entity's); null: it has none.
 */
export function ItemLines({
  space,
  item,
  entity,
}: {
  space: SpaceOut;
  item: Pick<ItemRow, "id" | "title"> & Partial<Pick<ItemRow, "kind" | "file">>;
  entity?: { name: string; icon?: EntityIcon | null } | null | undefined;
}) {
  return (
    <span class="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2">
      <PeekKindIcon space={space} item={item} />
      <Link href={spaceUrl(space, `/items/${item.id}`)} class={`${LINK} [overflow-wrap:anywhere]`}>
        {titleOf(item)}
      </Link>
      {entity === undefined ? null : usesPills() ? (
        <span class="col-start-2">
          <EntityLabel entity={entity} />
        </span>
      ) : (
        <>
          <span class="flex h-4 items-center justify-center">
            <EntityGlyph icon={entity?.icon ?? null} size={14} empty={entity ? "dot" : "none"} />
          </span>
          <span class="truncate text-xs text-zinc-500">{entity?.name ?? t("items.noEntity")}</span>
        </>
      )}
    </span>
  );
}

/** `KindIcon` within a line of text (a link in a comment): its glyph alone, 14px, filling the room made for it. */
export function InlineKindIcon({ item }: { item: Pick<ItemRow, "kind" | "file" | "title"> }) {
  return <TypeGlyph kind={item.kind} mime={item.file?.mime} name={fileNameOf(item)} size={14} />;
}

/**
 * What an item is, as `KindIcon` draws it, from what's known of it: its kind, its file's type and name (a kind this
 * app doesn't know: an item's icon). The glyph alone, decorative.
 */
export function TypeGlyph(p: { kind: string; mime?: string | undefined; name: string; size?: number }) {
  const sort = p.kind === "file" ? fileType(p.mime, p.name) : null;
  const Glyph = sort ? FILE_ICONS[sort] : (KIND_ICONS[p.kind as ItemRow["kind"]] ?? NAV_ICONS.items);
  return <Glyph size={p.size ?? 16} class={`block ${sort ? FILE_COLORS[sort] : "text-zinc-500"}`} />;
}

/** A paperclip, for the attachments column's header. */
export function PaperclipIcon({ label }: { label: string }) {
  return (
    <span class="flex h-5 items-center" title={label}>
      <Paperclip />
      <span class="sr-only">{label}</span>
    </span>
  );
}

/** A paperclip and how many files are attached; nothing without attachments. */
export function AttachmentCount({ n }: { n: number }) {
  if (n === 0) {
    return null;
  }
  const label = t("attachments.count", { count: n });
  return (
    <span class="flex h-5 items-center gap-0.5 text-xs whitespace-nowrap text-zinc-500" title={label}>
      <Paperclip />
      <span class="sr-only">{label}</span>
      <span aria-hidden="true">{n}</span>
    </span>
  );
}
